import Vue, { ComponentOptions } from 'vue';
import { PiniaVuePlugin } from 'pinia';
import App from './App.vue';
import router from './router';
import pinia from './stores/index';
import './assets/icons';
import './Services/GlobalVariables';
// usageTracker (AB#4279 / FE-4 — Yango usage tracking workstream):
// Direct import is used here so we can call `usageTracker.init()` once
// the router is ready. The tracker is also exposed on `window.usageTracker`
// (see GlobalVariables.ts, FE-2) so that `.vue` SFCs (LogIn.vue, Otp.vue,
// InvoiceStatus.vue, etc.) can call `window.usageTracker.track(...)`
// without re-importing. The `init()` call sets up the 30s flush interval
// and the `beforeunload` best-effort final flush; it is safe to call
// exactly once at app boot. We chain it off `router.onReady()` (mirrors
// the Libertas `router.isReady().then(...)` pattern in semantics, but
// uses the vue-router 3.x callback API — `isReady()` is a vue-router 4
// API that does not exist on Yango's `vue-router@^3.5.4`) so the first
// page-view event emitted by the `afterEach` hook in `src/router/index.ts`
// is captured in the queue.
import { usageTracker } from './Services/usageTracker';
import { registerSW } from 'virtual:pwa-register';

// Register the Vite PWA service worker. Type is provided by
// `vite-plugin-pwa/client` (declared in tsconfig.json `types`).
registerSW({
  immediate: true,
  /**
   * Vite PWA lifecycle callback — fires once the service worker is registered and its script URL is known.
   *
   * What: Logs the resolved SW URL to the browser console for deployment verification (Cloudflare Pages path, cache bust hash).
   * Why: Yango has no dedicated PWA observability dashboard; console output is the first-line check that `vite-plugin-pwa`
   *      emitted the expected worker under `/sw.js` or workbox-generated equivalent. Mirrors Libertas boot diagnostics pattern.
   * Assumptions:
   * - `swUrl` is a same-origin relative or absolute URL string supplied by `virtual:pwa-register` (typed by `vite-plugin-pwa/client`).
   * - Registration is triggered with `immediate: true` at module load — this callback may run before `new Vue(...).$mount`.
   * Edge cases:
   * - In dev mode (`vite dev`), registration may be skipped or use a dev-specific worker; the callback might not fire — that is expected.
   * - Repeated hot-module reloads in dev can register multiple workers; production builds register exactly once per tab session.
   * - This handler performs no side effects beyond logging; SW update prompts and skip-waiting are not configured here.
   *
   * @param swUrl {string} Resolved service-worker script URL at registration time.
   * @returns {void}
   */
  onRegisteredSW(swUrl: string): void {
    console.log('Service Worker registered at: ' + swUrl);
  },
  /**
   * Vite PWA lifecycle callback — fires when the precache manifest is fully populated and static assets are offline-ready.
   *
   * What: Logs a single readiness line so operators know the SPA shell (HTML, JS, CSS, icons) can boot without network.
   * Why: Report users on intermittent connectivity (store Wi‑Fi) need confidence that cached routes load; this is the
   *      earliest client-side signal that precaching completed successfully.
   * Assumptions:
   * - `registerSW({ immediate: true })` runs at import time, before Vue mount — offline shell is available even if API calls fail.
   * - API traffic (`/Reports/**`, `/api/**`) is not precached and still requires connectivity; only the SPA assets are offline-capable.
   * Edge cases:
   * - First visit ever: callback fires only after initial precache download completes (may lag first paint).
   * - SW update with changed precache hash: callback may fire again after the new worker activates.
   * - Private browsing / blocked storage: registration can fail silently; this callback never runs — no error surface here.
   *
   * @returns {void}
   */
  onOfflineReady(): void {
    console.log('Yango PWA ready to work offline');
  },
});

// Centralized CSS imports for proper loading order.
// Order is intentional: bootstrap first (foundation), then
// vue-good-table styles, then app-specific overrides last.
import 'bootstrap/dist/css/bootstrap.min.css';
import 'vue-good-table/dist/vue-good-table.css';
import './assets/main.css';

// Bootstrap JS is required for collapse, dropdown, and modal
// behaviors used by the layout components.
import 'bootstrap/dist/js/bootstrap.min';
import DrawerLayout from 'vue-drawer-layout';
Vue.use(DrawerLayout);
Vue.use(PiniaVuePlugin);

// Vue 2.7's `ComponentOptions` interface does not include `pinia` and
// `router` keys (those are injected by Pinia/Vue Router at runtime and
// are not part of the upstream Vue type definitions). To preserve the
// runtime contract without disabling strict mode, we declare a local
// `RootComponentOptions` interface that extends `ComponentOptions<Vue>`
// with the two runtime-injected keys typed against the concrete
// pinia/router instances.
interface RootComponentOptions extends ComponentOptions<Vue> {
  pinia: typeof pinia;
  router: typeof router;
}
const rootOptions: RootComponentOptions = {
  pinia,
  router,
  render: (h): Vue.VNode => h(App),
};
new Vue(rootOptions).$mount('#app');

// Boot the client-side usage tracker once the router is ready. The
// `router.onReady(cb, errorCb)` callback fires after the initial
// navigation has completed (or failed), which guarantees the
// `afterEach` hook registered in `src/router/index.ts` has been
// installed and the first page-view event will be enqueued by the
// tracker rather than dropped on the floor. `onReady` is the
// vue-router 3.x equivalent of the vue-router 4 `isReady(): Promise`
// API; it is callback-based and does not return a Promise. Mirrors
// the Libertas reference implementation (`Libertas - FrontEnd/
// src/main.js:19-21`) in semantics. The callback never throws:
// `init()` is idempotent (guarded by an `initialised` flag) and the
// `beforeunload` listener registration is synchronous. We omit the
// `errorCb` argument because the callback itself cannot throw; if
// the initial navigation fails, the router-internal failure is an
// unrecoverable SPA-wide condition (the app cannot render at all),
// and surfacing it via a local handler would not help — we let it
// bubble to the default unhandled-rejection path. There is no
// `errorCaptured` hook in `App.vue` and no `Vue.config.errorHandler`
// is set, so there is no custom error surface to route to.
router.onReady((): void => {
  usageTracker.init();
});
